import { withAuth } from '../components/withAuth';
import { NextPage } from 'next';
import Head from 'next/head';
import Chart from '../components/Chart';
import React, { useState } from 'react';

const OrderRequest: NextPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tankOption, setTankOption] = useState('');
  const [activeTankId, setActiveTankId] = useState<number | null>(null);
  const [gallons, setGallons] = useState('');
  const [tankLevel, setTankLevel] = useState('');
  const [instructions, setInstructions] = useState('');

  const toggleModal = () => {
    if (!isModalOpen) {
      setTankOption('');
      setGallons('');
      setTankLevel('');
      setInstructions('');
    }
    setIsModalOpen(!isModalOpen);
  };

  const [tanks, setTanks] = useState([
    {
      id: 1,
      type: 'Horizontal 120',
      fuel: 'Heating Oil',
      capacity: '120 gal',
      location: '100 River Avenue',
      city: 'Aniak, Alaska 99557',
      currentLevel: '~60 gal',
      readingDate: '5/8/2024',
      percentage: '50%',
      status: 'Leased',
      lastDelivery: '10/9/2023',
      checked: true,
      refillRequested: false
    },
    {
      id: 2,
      type: 'Horizontal 500',
      fuel: 'Propane',
      capacity: '500 gal',
      location: '100 River Avenue',
      city: 'Aniak, Alaska 99557',
      currentLevel: '~380 gal',
      readingDate: '5/8/2024',
      percentage: '76%',
      status: 'Leased',
      lastDelivery: '11/13/2023',
      checked: false,
      refillRequested: false
    },
    {
      id: 3,
      type: 'Horizontal 2000',
      fuel: 'Diesel',
      capacity: '2000 gal',
      location: '100 River Avenue',
      city: 'Aniak, Alaska 99557',
      currentLevel: '~640 gal',
      readingDate: '5/8/2024',
      percentage: '32%',
      status: 'Leased',
      lastDelivery: '12/18/2023',
      checked: false,
      refillRequested: false
    }
  ]);

  const handleSubmit = (event: any) => {
    event.preventDefault(); // Prevent the form from actually submitting
    setTanks(
      tanks.map((tank) =>
        tank.id === activeTankId ? { ...tank, refillRequested: true } : tank
      )
    );
    setIsModalOpen(false);
    setActiveTankId(null); // Reset the active tank ID
  };

  return (
    <>
      <Head>
        <title>Crowley | Request a Delivery</title>
      </Head>
      {/* OVERLAY */}
      <div
        className={`fixed inset-0 bg-gray-500 bg-opacity-50 ${
          isModalOpen ? 'block' : 'hidden'
        }`}
        onClick={toggleModal}
      ></div>
      <div className="max-w-screen-xl container mx-auto p-4">
        {/* PAGE TITLE */}
        <div className="max-w-screen-xl mx-auto mt-4 mb-8">
          <h2 className="text-3xl font-semibold">Request a Delivery</h2>
        </div>

        {/* TANK INVENTORY */}
        <section className="tank-refill my-8">
          <p className="text-2xl font-semibold mb-4">Tank Inventory</p>
          <div className="bg-white rounded-lg shadow-md p-2">
            {tanks.map((tank) => (
              <div key={tank.id} className="flex flex-col md:flex-row">
                <div
                  id="select"
                  className="border-2 border-white bg-slate-50 p-5 flex items-center flex-grow justify-center"
                >
                  <button
                    type="button"
                    className={`focus:outline-none text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text px-5 py-2.5 ${
                      tank.refillRequested
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    onClick={() => {
                      setTankOption('');
                      setGallons('');
                      setTankLevel('');
                      setInstructions('');
                      setActiveTankId(tank.id);
                      setIsModalOpen(true);
                    }}
                    disabled={tank.refillRequested}
                  >
                    {tank.refillRequested ? 'Refill Requested' : 'Order Refill'}
                  </button>
                </div>

                <div
                  id="type"
                  className="border-2 border-white bg-slate-50 p-5 flex-grow"
                >
                  <p>
                    <span className="font-semibold leading-8">Tank: </span>{' '}
                    {tank.type}
                  </p>
                  <p>
                    <span className="font-semibold leading-8">Fuel: </span>{' '}
                    {tank.fuel}
                  </p>
                  {/* <p><span className='font-semibold leading-8'>Capacity: </span>{tank.capacity}</p> */}
                </div>

                <div
                  id="location"
                  className="border-2 border-white bg-slate-50 p-5 flex-grow"
                >
                  <p className="font-semibold leading-8">Location:</p>
                  <p>{tank.location}</p>
                  <p>{tank.city}</p>
                </div>

                <div
                  id="reading"
                  className="border-2 border-white bg-slate-50 p-5"
                  style={{ minWidth: '200px' }}
                >
                  <div className="flex justify-between mb-1">
                    <span className="text-base font-medium text-blue-700">
                      {tank.currentLevel}
                    </span>
                  </div>
                  <div className="w-full h-6 bg-gray-200 rounded-full mt-2">
                    <div
                      className="bg-blue-600 h-6 text-sm font-medium text-blue-100 text-center p-0.5 rounded-full"
                      style={{ width: tank.percentage }}
                    >
                      {tank.percentage}
                    </div>
                  </div>
                  <p className="text-sm leading-8">
                    Reading Date: {tank.readingDate}
                  </p>
                </div>

                <div
                  id="status"
                  className="border-2 border-white bg-slate-50 p-5 flex-grow"
                >
                  <p>
                    <span className="font-semibold leading-8">Status:</span>{' '}
                    {tank.status}
                  </p>
                  <p>
                    <span className="font-semibold leading-8">
                      Last Delivery:
                    </span>{' '}
                    {tank.lastDelivery}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* GRAPH - TANK LEVEL HISTORY */}
        <section className="tank-level-history my-8">
          <p className="text-2xl font-semibold my-4">Tank Level History</p>
          <Chart></Chart>
        </section>
      </div>

      {/* Main modal */}
      <div
        id="crud-modal"
        tabIndex={-1}
        aria-hidden={!isModalOpen}
        className={`fixed top-0 right-0 left-0 z-50 justify-center items-center w-full md:inset-0 h-modal md:h-full ${
          isModalOpen ? 'flex' : 'hidden'
        }`}
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
      >
        <div className="relative p-4 w-full max-w-md max-h-full m-auto z-50">
          {/* Modal content */}
          <div className="relative bg-white rounded-lg shadow">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Delivery Details
              </h3>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
                onClick={toggleModal}
              >
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7L1 13"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>
            {/* Modal body */}
            <form onSubmit={handleSubmit} className="p-4 md:p-5">
              <div className="grid gap-4 mb-4 grid-cols-4">
                {/* FILL OR SPECIFY AMOUNT */}
                <div className="col-span-4">
                  <div className="mb-4">
                    <label
                      htmlFor=""
                      className="block text-sm font-medium text-gray-900 mb-2"
                    >
                      Would you like to fill the tank or specify a number of
                      gallons?
                    </label>
                  </div>

                  {/* Radio Button 1 - Fill my tank */}
                  <div className="mb-2">
                    <input
                      id="fill-tank"
                      type="radio"
                      value="fill"
                      name="tank-option"
                      checked={tankOption === 'fill'}
                      onChange={() => setTankOption('fill')}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                      required
                    />
                    <label
                      htmlFor="fill-tank"
                      className="ml-2 text-sm font-medium text-gray-900"
                    >
                      Fill my tank
                    </label>
                  </div>

                  {/* Radio Button 2 - Specify a number of gallons */}
                  <div className="mb-2">
                    <input
                      id="specify-gallons"
                      type="radio"
                      min="1"
                      step="1"
                      value="specify"
                      name="tank-option"
                      checked={tankOption === 'specify'}
                      onChange={() => setTankOption('specify')}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2 "
                    />
                    <label
                      htmlFor="specify-gallons"
                      className="ml-2 text-sm font-medium text-gray-900"
                    >
                      Specify a number of gallons
                    </label>
                    {tankOption === 'specify' && (
                      <div className="mt-2 ml-6">
                        <input
                          type="number"
                          id="gallons"
                          name="gallons"
                          value={gallons} 
                          onChange={e => setGallons(e.target.value)}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                          placeholder="Enter a number"
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* ENTER TANK LEVEL */}
                <div className="col-span-4 sm:col-span-3">
                  <label
                    htmlFor="tank-level"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Enter current tank level
                  </label>
                  <input
                    type="number"
                    id="tank-level"
                    name="tank-level"
                    value={tankLevel}
                    onChange={e => setTankLevel(e.target.value)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                    placeholder="Enter a number"
                    required
                  />
                </div>
                {/* UNIT TYPE */}
                <div className="col-span-4 sm:col-span-1">
                  <label
                    htmlFor="unit"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Unit type
                  </label>
                  <select
                    id="unit"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5"
                  >
                    <option selected>gal</option>
                    <option value="TV">%</option>
                  </select>
                </div>
                {/* SPECIAL REQUEST */}
                <div className="col-span-4">
                  <label
                    htmlFor="instructions"
                    className="block mb-2 text-sm font-medium text-gray-900"
                  >
                    Enter any special requests or additional instructions
                  </label>
                  <textarea
                    id="instructions"
                    rows={4}
                    value={instructions}
                    onChange={e => setInstructions(e.target.value)}
                    className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Write instructions here."
                  ></textarea>
                </div>
              </div>
              <div className="flex justify-center">
                <button
                  type="submit"
                  className="text-white inline-flex items-center bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                >
                  Submit Order Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(OrderRequest);
